"use client";

import React, { useState } from 'react';
import {
  BookOpenIcon,
  CheckBadgeIcon,
  ChevronDownIcon,
  LockClosedIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type {
  AgentKnowledgeClarification,
  AgentKnowledgeDisplay,
  AgentKnowledgePreference,
} from '@/lib/agentKnowledgeDisplay';
import type { ClarifyType } from '@/lib/chat/clarify';

type PreferenceField = 'spokesperson' | 'mediaContact' | 'tone' | 'styleNotes' | 'locale' | null;

type WhatAgentKnowsPanelProps = {
  model: AgentKnowledgeDisplay;
  loading?: boolean;
  onEditPreference: (field?: PreferenceField) => void;
  onOpenClarifications: () => void;
  onClearClarification: (type: ClarifyType) => void;
  className?: string;
};

const EDITABLE_FIELDS: Record<string, PreferenceField> = {
  spokesperson: 'spokesperson',
  mediaContact: 'mediaContact',
  tone: 'tone',
  styleNotes: 'styleNotes',
  locale: 'locale',
};

function formatConfidence(value?: number): string | null {
  if (typeof value !== 'number') return null;
  return `${Math.round(value * 100)}%`;
}

function truncate(value: string, max = 84): string {
  return value.length > max ? `${value.slice(0, max - 1)}...` : value;
}

function PreferenceRow({
  preference,
  onEditPreference,
}: {
  preference: AgentKnowledgePreference;
  onEditPreference: (field?: PreferenceField) => void;
}) {
  const confidence = formatConfidence(preference.confidence);
  const editableField = EDITABLE_FIELDS[preference.field];
  return (
    <div className="rounded-lg border border-border/50 bg-background/60 p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="text-xs font-semibold text-foreground">{preference.label}</p>
            {preference.reviewGated ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                <LockClosedIcon className="h-3 w-3" />
                Review gated
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{truncate(preference.value)}</p>
          {(preference.source || confidence) && (
            <p className="mt-1 text-[11px] text-muted-foreground/80">
              {preference.source ? `Source ${preference.source}` : ''}
              {preference.source && confidence ? ' · ' : ''}
              {confidence ? `Confidence ${confidence}` : ''}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => onEditPreference(editableField || undefined)}
          disabled={!editableField}
          title={editableField ? 'Edit preference' : 'Edit this preference in the client profile'}
        >
          <PencilSquareIcon className="h-3.5 w-3.5" />
          Edit
        </Button>
      </div>
    </div>
  );
}

function ClarificationRow({
  clarification,
  onClear,
}: {
  clarification: AgentKnowledgeClarification;
  onClear: (type: ClarifyType) => void;
}) {
  return (
    <div className="rounded-lg border border-border/50 bg-background/60 p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground">{clarification.label}</p>
          <p className={cn('mt-1 text-xs text-muted-foreground', clarification.skipped && 'italic')}>
            {truncate(clarification.text)}
          </p>
          {clarification.placeholderCount > 0 ? (
            <p className="mt-1 text-[11px] text-muted-foreground/80">
              {clarification.placeholderCount} placeholders accepted
            </p>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => onClear(clarification.type)}
        >
          <TrashIcon className="h-3.5 w-3.5" />
          Clear
        </Button>
      </div>
    </div>
  );
}

export function WhatAgentKnowsPanel({
  model,
  loading,
  onEditPreference,
  onOpenClarifications,
  onClearClarification,
  className,
}: WhatAgentKnowsPanelProps) {
  const [expanded, setExpanded] = useState(false);

  if (!model.hasKnowledge && !loading) return null;

  const summaryItems = [
    { label: 'Preferences', count: model.preferences.length },
    { label: 'Clarifications', count: model.clarifications.length },
    { label: 'Sources', count: model.memories.length },
  ];

  return (
    <section className={cn('rounded-xl border border-border/50 bg-background/80 shadow-sm', className)}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-primary/30"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <BookOpenIcon className="h-4 w-4 text-primary" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">What the agent knows</span>
            <span className="block truncate text-xs text-muted-foreground">
              Applied context for {model.clientLabel}
            </span>
          </span>
          <span className="ml-auto hidden flex-wrap items-center gap-1.5 lg:flex">
            {summaryItems.map((item) => (
              <span
                key={item.label}
                className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground"
              >
                {item.label}
                <span className="font-semibold tabular-nums text-foreground">{item.count}</span>
              </span>
            ))}
          </span>
          <ChevronDownIcon className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-180')} />
        </button>
        <Button variant="ghost" size="sm" className="h-8 shrink-0 px-2 text-xs" onClick={() => onEditPreference()}>
          Manage preferences
        </Button>
      </div>

      {!expanded && (
        <div className="flex flex-wrap gap-1.5 border-t border-border/40 px-3 py-2 lg:hidden">
          {summaryItems.map((item) => (
            <span
              key={item.label}
              className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground"
            >
              {item.label}
              <span className="font-semibold tabular-nums text-foreground">{item.count}</span>
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div className="border-t border-border/40 p-3">
          {loading && !model.hasKnowledge ? (
            <p className="text-xs text-muted-foreground">Loading client memory...</p>
          ) : (
            <div className="grid gap-3 lg:grid-cols-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferences</p>
              <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                {model.preferences.length}
              </span>
            </div>
            {model.preferences.length ? (
              <div className="space-y-2">
                {model.preferences.slice(0, 4).map((preference) => (
                  <PreferenceRow
                    key={`${preference.field}:${preference.value}`}
                    preference={preference}
                    onEditPreference={onEditPreference}
                  />
                ))}
                {model.preferences.length > 4 ? (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onEditPreference()}>
                    Show all preferences
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No saved client preferences yet.</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Clarifications</p>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onOpenClarifications}>
                Open
              </Button>
            </div>
            {model.clarifications.length ? (
              <div className="space-y-2">
                {model.clarifications.slice(0, 3).map((clarification) => (
                  <ClarificationRow
                    key={clarification.type}
                    clarification={clarification}
                    onClear={onClearClarification}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No stored clarifications for this thread.</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Memory and sources</p>
              <span className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                {model.memories.length}
              </span>
            </div>
            {model.memories.length ? (
              <div className="space-y-2">
                {model.memories.slice(0, 5).map((memory) => (
                  <div key={memory.id} className="rounded-lg border border-border/50 bg-background/60 p-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <CheckBadgeIcon className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <p className="truncate text-xs font-semibold text-foreground">{truncate(memory.label, 60)}</p>
                        </div>
                        {(memory.source || typeof memory.confidence === 'number') && (
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {memory.source ? `Source ${memory.source}` : ''}
                            {memory.source && typeof memory.confidence === 'number' ? ' · ' : ''}
                            {typeof memory.confidence === 'number' ? `Confidence ${formatConfidence(memory.confidence)}` : ''}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled
                        title="Memory deletion is not available from this surface yet."
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No memory references used in recent runs.</p>
            )}
          </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
